<script lang="ts">
  import { api } from '$shared/api';
  import type { Artist, ArtistTrack } from '$lib/types';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';

  let { slug }: { slug: string } = $props();

  let artist = $state<Artist | null>(null);
  let tracks = $state<ArtistTrack[]>([]);
  let loading = $state(true);
  let error = $state('');

  $effect(() => {
    loadArtist();
  });

  async function loadArtist() {
    loading = true;
    error = '';
    try {
      const res = await api.get<{ artist: Artist; tracks: ArtistTrack[] }>(`/artists/${slug}/tracks`);
      artist = res.artist;
      tracks = res.tracks;
    } catch (e) {
      error = (e as Error).message || 'Artist not found';
    } finally {
      loading = false;
    }
  }

  function formatCount(n: number): string {
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1_000) return (n / 1_000).toFixed(n >= 10_000 ? 0 : 1).replace(/\.0$/, '') + 'K';
    return String(n);
  }
</script>

<div class="page">
  {#if loading}
    <LoadingSkeleton lines={8} />
  {:else if error}
    <h1>Artist</h1>
    <p class="empty">{error}</p>
    <button type="button" class="back-link" onclick={() => history.back()}>&larr; Back</button>
  {:else if artist}
    <div class="artist-header">
      {#if artist.image_url}
        <img src={artist.image_url} alt={artist.name} class="artist-avatar" />
      {:else}
        <div class="artist-avatar artist-avatar--placeholder">
          {artist.name.charAt(0).toUpperCase()}
        </div>
      {/if}
      <div class="artist-info">
        <h1 class="artist-name">{artist.name}</h1>
        {#if artist.bio}
          <p class="artist-bio">{artist.bio}</p>
        {/if}
      </div>
    </div>

    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-value">{formatCount(artist.total_votes)}</div>
        <div class="stat-label">Votes</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{artist.track_count}</div>
        <div class="stat-label">Tracks</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{artist.duel_count}</div>
        <div class="stat-label">Duels</div>
      </div>
      {#if artist.avg_ai_score}
        <div class="stat-card">
          <div class="stat-value">{artist.avg_ai_score}</div>
          <div class="stat-label">Avg AI Score</div>
        </div>
      {/if}
    </div>

    {#if artist.avg_bpm || artist.top_key}
      <div class="acoustic-profile">
        <h3 class="section-heading">Acoustic Profile</h3>
        <div class="acoustic-tags">
          {#if artist.avg_bpm}
            <span class="tag tag--acoustic">{Math.round(artist.avg_bpm)} BPM avg</span>
          {/if}
          {#if artist.top_key}
            <span class="tag tag--acoustic">{artist.top_key}</span>
          {/if}
        </div>
      </div>
    {/if}

    {#if tracks.length > 0}
      <h3 class="section-heading">Tracks on Beat Duel</h3>
      <div class="track-list">
        {#each tracks as track}
          <div class="track-row">
            <div class="track-main">
              <a href="#/duels/{track.duel_id}" class="track-name">{track.name || 'Untitled'}</a>
              <div class="track-meta-row">
                <span class="track-sub">in {track.duel_name}</span>
                <span class="track-sub"
                  >&middot; submitted by <a href="#/users/{track.submitted_by}">{track.submitted_by}</a></span
                >
              </div>
            </div>
            <div class="track-stats">
              {#if track.vote_count > 0}
                <span class="vote-pill">{track.vote_count} vote{track.vote_count !== 1 ? 's' : ''}</span>
              {/if}
              {#if track.ai_score}
                <a href="#/tracks/{track.id}/ai-score" class="ai-pill">AI {track.ai_score}</a>
              {/if}
              {#if track.bpm}
                <span class="bpm-pill">{Math.round(track.bpm)} BPM</span>
              {/if}
            </div>
          </div>
        {/each}
      </div>
    {:else}
      <p class="empty">No tracks found for this artist.</p>
    {/if}

    <div class="artist-actions">
      <button type="button" class="back-link" onclick={() => history.back()}>&larr; Back</button>
    </div>
  {/if}
</div>

<style>
  .page {
    max-width: 700px;
    margin: 0 auto;
  }

  .artist-header {
    display: flex;
    gap: 1.25rem;
    align-items: center;
    margin-bottom: 1.5rem;
  }
  .artist-avatar {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid var(--border);
    flex-shrink: 0;
  }
  .artist-avatar--placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, var(--pink), var(--purple));
    color: #fff;
    font-size: 2rem;
    font-weight: 800;
  }
  .artist-name {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0;
    background: linear-gradient(135deg, var(--pink), var(--purple));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .artist-bio {
    color: var(--text-secondary);
    font-size: 0.875rem;
    margin-top: 0.25rem;
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }
  .stat-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 1rem;
    text-align: center;
  }
  .stat-value {
    font-size: 1.5rem;
    font-weight: 800;
    color: var(--text);
  }
  .stat-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    color: var(--text-muted);
    letter-spacing: 0.05em;
    margin-top: 0.25rem;
  }

  .section-heading {
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--text-muted);
    margin: 1.5rem 0 0.75rem;
  }

  .acoustic-profile {
    margin-bottom: 1rem;
  }
  .acoustic-tags {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
  .tag--acoustic {
    padding: 0.2rem 0.6rem;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 600;
    background: rgba(139, 92, 246, 0.15);
    color: #8b5cf6;
  }

  .track-list {
    display: flex;
    flex-direction: column;
  }
  .track-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px solid var(--border);
    gap: 1rem;
  }
  .track-main {
    flex: 1;
    min-width: 0;
  }
  .track-name {
    color: var(--text);
    font-weight: 500;
    font-size: 0.9rem;
  }
  .track-name:hover {
    color: var(--pink);
  }
  .track-meta-row {
    display: flex;
    gap: 0.25rem;
    flex-wrap: wrap;
  }
  .track-sub {
    font-size: 0.75rem;
    color: var(--text-muted);
  }
  .track-sub a {
    color: var(--cyan);
  }

  .track-stats {
    display: flex;
    gap: 0.375rem;
    align-items: center;
    flex-shrink: 0;
  }
  .vote-pill,
  .ai-pill,
  .bpm-pill {
    padding: 0.125rem 0.4rem;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 600;
  }
  .vote-pill {
    background: rgba(0, 229, 255, 0.1);
    color: var(--cyan);
  }
  .ai-pill {
    background: rgba(16, 185, 129, 0.1);
    color: #10b981;
    text-decoration: none;
  }
  .bpm-pill {
    background: rgba(139, 92, 246, 0.1);
    color: #8b5cf6;
  }

  @media (max-width: 480px) {
    .artist-header {
      flex-direction: column;
      text-align: center;
    }
    .artist-avatar {
      width: 64px;
      height: 64px;
    }
    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }
    .track-row {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.5rem;
    }
    .track-stats {
      width: 100%;
    }
  }
  .empty {
    color: var(--text-muted);
  }
  .artist-actions {
    margin-top: 1.5rem;
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  .back-link {
    font-size: 0.875rem;
    color: var(--cyan);
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
  }
</style>
